import { FC, useEffect, useMemo, useRef, useState } from 'react';
import { sortBy } from 'lodash';
import LoadingSpinner from 'components/LoadingSpinner/LoadingSpinner';
import { ReactComponent as DropdownIcon } from 'assets/icons/chevron-down.svg';
import { ReactComponent as SearchIcon } from 'assets/icons/search.svg';
import { ReactComponent as CancelIcon } from 'assets/icons/cancel.svg';
import { IProcedureCode } from 'interfaces/services';

const getProcedureLabel = (procedure: IProcedureCode) =>
  `${procedure.code} - ${procedure.name}`;

interface ProcedureCodeDropdownProps {
  procedures: IProcedureCode[];
  value: IProcedureCode[];
  onChange: (value: IProcedureCode[]) => void;
  isLoading?: boolean;
}

const ProcedureCodeDropdown: FC<ProcedureCodeDropdownProps> = ({
  procedures,
  value,
  onChange,
  isLoading,
}) => {
  const selectAllElementRef = useRef<HTMLInputElement | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [searchValue, setSearchValue] = useState('');

  const sortedProcedures = useMemo(
    () => sortBy(procedures, ['code']),
    [procedures]
  );

  const selectedCodes = value.map((procedure) => procedure.code);

  // MATCH AGAINST THE DISPLAYED LABEL; EVERY KEYWORD MUST APPEAR IN IT, IN ANY
  // ORDER, SO ONE SEARCH CAN COMBINE THE CODE AND THE NAME (E.G. "d0120 perio")
  const searchedProcedures = useMemo(() => {
    const keywords = searchValue.toLowerCase().split(/\s+/).filter(Boolean);
    if (keywords.length === 0) return sortedProcedures;

    return sortedProcedures.filter((procedure) => {
      const label = getProcedureLabel(procedure).toLowerCase();
      return keywords.every((keyword) => label.includes(keyword));
    });
  }, [sortedProcedures, searchValue]);

  const searchedSelectedCount = searchedProcedures.filter((procedure) =>
    selectedCodes.includes(procedure.code)
  ).length;

  const isAllSelected =
    searchedProcedures.length > 0 &&
    searchedSelectedCount === searchedProcedures.length;

  useEffect(() => {
    if (!selectAllElementRef.current) return;
    selectAllElementRef.current.indeterminate =
      searchedSelectedCount > 0 && !isAllSelected;
  }, [searchedSelectedCount, isAllSelected]);

  const closeDropdown = () => {
    setIsOpen(false);
    setSearchValue('');
  };

  // CLOSE ON A COMPLETED OUTSIDE CLICK (NOT ON FOCUS LOSS OR MOUSEDOWN). THE LIST
  // IS IN THE MODAL FLOW, SO CLOSING EARLIER SHIFTS THE BUTTONS UNDER THE CURSOR
  // AND SWALLOWS THE CLICK (E.G. UPDATE NEEDING TWO CLICKS)
  useEffect(() => {
    if (!isOpen) return;

    const onClickOutside = (e: MouseEvent) => {
      // USE THE PATH RECORDED AT DISPATCH SO CLICKS ON ELEMENTS REMOVED BY THE
      // CLICK ITSELF (E.G. THE CLEAR SEARCH ICON) STILL COUNT AS INSIDE
      if (dropdownRef.current && e.composedPath().includes(dropdownRef.current))
        return;
      closeDropdown();
    };

    document.addEventListener('click', onClickOutside);
    return () => {
      document.removeEventListener('click', onClickOutside);
    };
  }, [isOpen]);

  const handleOnChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    procedure: IProcedureCode
  ) => {
    const updatedValue = e.currentTarget.checked
      ? [...value, procedure]
      : value.filter(
          (selectedProcedure) => selectedProcedure.code !== procedure.code
        );

    onChange(updatedValue);
  };

  // SELECT ALL ONLY AFFECTS THE SEARCHED PROCEDURES AND KEEPS OTHER SELECTIONS
  const handleOnSelectAll = (e: React.ChangeEvent<HTMLInputElement>) => {
    const searchedCodes = searchedProcedures.map((procedure) => procedure.code);
    const unsearchedSelectedProcedures = value.filter(
      (procedure) => !searchedCodes.includes(procedure.code)
    );

    onChange(
      e.currentTarget.checked
        ? [...unsearchedSelectedProcedures, ...searchedProcedures]
        : unsearchedSelectedProcedures
    );
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        type="button"
        aria-expanded={isOpen}
        disabled={isLoading}
        onClick={() => (isOpen ? closeDropdown() : setIsOpen(true))}
        className={`relative w-full h-[4.8rem] pl-1.6 rounded-[1rem] bg-white border-[0.5px] border-light-grey text-left outline-none shadow-primary border-lightest-grey ${
          isLoading
            ? 'opacity-50 cursor-not-allowed bg-lightest-grey'
            : 'cursor-pointer hover:border-magenta hover:shadow-input-active focus:border-magenta focus:shadow-input-active'
        } ${value.length === 0 ? 'text-mid-grey' : 'text-darkest-grey'}`}
      >
        <span className="flex items-center gap-x-1 pr-3">
          {value.length === 0
            ? 'Select Procedure Codes'
            : `${value.length} of ${sortedProcedures.length} selected`}
          {isLoading && (
            <LoadingSpinner className="all-child:fill-magenta w-2! h-2!" />
          )}
        </span>
        <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
          <DropdownIcon className="w-1.4 h-1.4" />
        </span>
      </button>
      {isOpen && (
        <div className="mt-0.5 w-full rounded-[0.8rem] bg-white text-base shadow-primary flex flex-col gap-y-1.6 p-2 max-h-[28rem] overflow-auto scrollbar">
          <div className="flex flex-row">
            <SearchIcon className="mr-[1rem]" width={20} />
            <input
              value={searchValue}
              placeholder="Search ..."
              className="text-14 placeholder-mid-grey focus:outline-none focus-visible:outline-none w-full"
              onChange={(e) => setSearchValue(e.target.value)}
            />
            {searchValue && (
              <CancelIcon
                className="ml-[1rem] cursor-pointer"
                width={20}
                onClick={() => setSearchValue('')}
              />
            )}
          </div>
          {searchedProcedures.length === 0 ? (
            <span className="text-14 text-mid-grey">No data</span>
          ) : (
            <div className="flex gap-x-1.6 items-center">
              <input
                type="checkbox"
                className="w-1.8 h-1.8"
                id="selectAll-procedure-codes"
                ref={selectAllElementRef}
                onChange={handleOnSelectAll}
                checked={isAllSelected}
              />
              <label
                className="flex gap-x-1.6 cursor-pointer font-bold text-14 leading-[2.1rem]"
                htmlFor="selectAll-procedure-codes"
              >
                Select All
              </label>
            </div>
          )}
          {searchedProcedures.map((procedure) => (
            <div
              key={procedure.code}
              className="flex gap-x-1.6 items-center"
            >
              <input
                type="checkbox"
                className="w-1.8 h-1.8 shrink-0"
                id={`procedure-code-${procedure.code}`}
                onChange={(e) => handleOnChange(e, procedure)}
                checked={selectedCodes.includes(procedure.code)}
              />
              <label
                className="cursor-pointer text-darkest-grey text-14 leading-[2.1rem]"
                htmlFor={`procedure-code-${procedure.code}`}
              >
                {getProcedureLabel(procedure)}
              </label>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ProcedureCodeDropdown;
